import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Shield,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Filter,
} from 'lucide-react';
import { apiGet } from '@/api/client';
import { PageHeader } from '@/components/shared/PageHeader';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { formatRelativeDate } from '@/lib/formatters';
import { queryKeys } from '@/api/queryKeys';
import type { SecuredRetrieval, RetrievalOutcome, SearchMode } from '@/types/retrieval';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface RetrievalsResponse {
  data: SecuredRetrieval[];
  meta: { pagination: { page: number; per_page: number; total: number; total_pages: number } };
}

// ---------------------------------------------------------------------------
// Search mode badge
// ---------------------------------------------------------------------------

const SEARCH_MODE_CONFIG: Record<
  SearchMode,
  { label: string; className: string }
> = {
  vector: {
    label: 'vector',
    className: 'bg-muted text-muted-foreground border-border',
  },
  keyword: {
    label: 'keyword',
    className: 'bg-status-info/10 text-status-info border-status-info/20',
  },
  hybrid: {
    label: 'hybrid',
    className: 'bg-plan-growth/10 text-plan-growth border-plan-growth/20',
  },
  grep: {
    label: 'grep',
    className: 'bg-status-warning/10 text-status-warning border-status-warning/20',
  },
};

function SearchModeBadge({ mode }: { mode: SearchMode | undefined }) {
  const safeMode: SearchMode = mode && mode in SEARCH_MODE_CONFIG ? mode : 'vector';
  const config = SEARCH_MODE_CONFIG[safeMode];
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium font-mono',
        config.className,
      )}
    >
      {config.label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Outcome badge
// ---------------------------------------------------------------------------

const OUTCOME_CONFIG: Record<
  RetrievalOutcome,
  { icon: typeof CheckCircle2; label: string; className: string; iconClass: string }
> = {
  allowed: {
    icon: CheckCircle2,
    label: 'Allowed',
    className: 'bg-status-allow/10 text-status-allow border-status-allow/30',
    iconClass: 'text-status-allow',
  },
  partial: {
    icon: AlertTriangle,
    label: 'Partial',
    className: 'bg-status-warning/10 text-status-warning border-status-warning/30',
    iconClass: 'text-status-warning',
  },
  denied: {
    icon: XCircle,
    label: 'Denied',
    className: 'bg-status-deny/10 text-status-deny border-status-deny/30',
    iconClass: 'text-status-deny',
  },
};

function OutcomeBadge({ outcome }: { outcome: RetrievalOutcome }) {
  const config = OUTCOME_CONFIG[outcome] ?? OUTCOME_CONFIG.denied;
  const Icon = config.icon;
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium',
        config.className,
      )}
    >
      <Icon className={cn('h-3 w-3', config.iconClass)} />
      {config.label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Filters
// ---------------------------------------------------------------------------

type OutcomeFilter = 'all' | RetrievalOutcome;

const OUTCOME_FILTERS: { value: OutcomeFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'allowed', label: 'Allowed' },
  { value: 'partial', label: 'Partial' },
  { value: 'denied', label: 'Denied' },
];

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function SecuredRetrievalsPage() {
  const [outcomeFilter, setOutcomeFilter] = useState<OutcomeFilter>('all');

  const { data: response, isLoading } = useQuery({
    queryKey: queryKeys.retrievals.list({ outcome: outcomeFilter }),
    queryFn: () =>
      apiGet<RetrievalsResponse>('/retrievals', {
        per_page: 50,
        ...(outcomeFilter !== 'all' ? { outcome: outcomeFilter } : {}),
      }),
  });

  const retrievals = response?.data ?? [];
  const total = response?.meta?.pagination?.total ?? 0;

  return (
    <div className="space-y-6 animate-fade-in">
      <PageHeader
        eyebrow="Observe"
        title="Secured Retrievals"
        subtitle="Monitor all retrieval operations: timestamps, principals, queries, search mode, policy evaluations, and outcomes."
      />

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap">
        <Filter className="h-4 w-4 text-muted-foreground" />
        {OUTCOME_FILTERS.map((f) => (
          <Button
            key={f.value}
            variant={outcomeFilter === f.value ? 'default' : 'outline'}
            size="sm"
            onClick={() => setOutcomeFilter(f.value)}
          >
            {f.label}
          </Button>
        ))}
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="space-y-2">
          {[1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} className="h-16" />
          ))}
        </div>
      ) : retrievals.length === 0 ? (
        <div className="rounded-lg border border-dashed p-12 text-center">
          <Shield className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-semibold">No retrievals found</h3>
          <p className="text-sm text-muted-foreground mt-1">
            {outcomeFilter === 'all'
              ? 'Retrieval events will appear here as your AI agents make secured requests.'
              : `No ${outcomeFilter} retrievals match the current filter.`}
          </p>
        </div>
      ) : (
        <div className="rounded-lg border overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">Query</th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground whitespace-nowrap">
                  Principal
                </th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground whitespace-nowrap">
                  Connector
                </th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground whitespace-nowrap">
                  Search Mode
                </th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">Outcome</th>
                <th className="px-4 py-3 text-right font-medium text-muted-foreground whitespace-nowrap">
                  Chunks
                </th>
                <th className="px-4 py-3 text-right font-medium text-muted-foreground whitespace-nowrap">
                  Latency
                </th>
                <th className="px-4 py-3 text-right font-medium text-muted-foreground">When</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {retrievals.map((r) => (
                <tr key={r.id} className="hover:bg-accent/50 transition-colors">
                  {/* Query */}
                  <td className="px-4 py-3 max-w-[260px]">
                    <p className="truncate font-mono text-xs text-foreground" title={r.query}>
                      {r.query}
                    </p>
                  </td>

                  {/* Principal, and who asserted it. principal_name is who the
                      caller SAID the end user was; Gateco does not verify that
                      claim, so the record also names the credential that made
                      it. Null on rows written before migration 051. */}
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className="text-xs">{r.principal_name}</span>
                    {r.actor_type && (
                      <span className="block text-[11px] text-muted-foreground">
                        {r.actor_type === 'api_key' ? 'via ' : 'by '}
                        {r.actor_name || (r.actor_type === 'api_key' ? 'API key' : 'console user')}
                      </span>
                    )}
                  </td>

                  {/* Connector */}
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className="text-xs">{r.connector_name}</span>
                  </td>

                  {/* Search Mode */}
                  <td className="px-4 py-3 whitespace-nowrap">
                    <SearchModeBadge mode={r.search_mode} />
                  </td>

                  {/* Outcome */}
                  <td className="px-4 py-3 whitespace-nowrap">
                    <OutcomeBadge outcome={r.outcome} />
                  </td>

                  {/* Chunks: allowed / matched */}
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <span className="text-xs tabular-nums">
                      <span className="text-status-allow">{r.allowed_chunks}</span>
                      <span className="text-muted-foreground"> / {r.matched_chunks}</span>
                    </span>
                  </td>

                  {/* Latency */}
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <span className="text-xs tabular-nums text-muted-foreground">
                      {r.latency_ms}ms
                    </span>
                  </td>

                  {/* Timestamp */}
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <span className="text-xs text-muted-foreground">
                      {formatRelativeDate(r.timestamp)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Footer count */}
      {!isLoading && (
        <p className="text-xs text-muted-foreground text-center">
          Showing {retrievals.length} of {total} retrievals
        </p>
      )}
    </div>
  );
}
